/**
 * Who is signed in to this browser (GET /api/me) and which sign-in methods the hub offers.
 *
 *   loading      not asked yet, or waiting for an owner link to be exchanged
 *   signed-in    me is set
 *   signed-out   the hub answered 401: show the sign-in screen
 *   unsupported  the hub has no auth module (404): behave as before, no user menu
 *   error        the hub could not be asked; the data layer shows its own error
 */
import { create } from 'zustand';
import { roleAtLeast } from '@orgus/protocol';
import type { AuthEndReason, AuthProviders, Me, Role } from '@orgus/protocol';
import { ApiError, toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { authClient } from './client.ts';
import { hasAuthFragment, parseInvite, parseSigninError } from './authHash.ts';
import type { SigninError } from './authHash.ts';

export type MeStatus = 'loading' | 'signed-in' | 'signed-out' | 'unsupported' | 'error';

/** Why the hub ended this browser's live stream and signed it out (SD-2); a role change does not. */
export type MeEndedBy = Exclude<AuthEndReason, 'role-changed'>;

/** Read once, before the data layer strips them from the address bar. */
const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
/** An owner link in the address bar (`#token=`). */
export const OWNER_LINK_RE = /(?:^|[#&])token=/;
let ownerLinkSeen = OWNER_LINK_RE.test(initialHash);

/**
 * True once the page has had an owner link in its address bar: at load, or pasted into the same
 * tab later. While the data layer exchanges it, asking /api/me would only answer 401.
 */
export function openedWithOwnerLink(): boolean {
  return ownerLinkSeen;
}

/**
 * A hash change in this tab: when it carries an owner link, note it, drop an earlier sign-in
 * error and start the exchange (`exchange` restarts the data layer, which reads the link).
 * Returns whether it did.
 */
export function takeOwnerLinkFromHash(hash: string, exchange: () => void): boolean {
  if (!OWNER_LINK_RE.test(hash)) return false;
  ownerLinkSeen = true;
  useMe.getState().clearSigninError();
  exchange();
  return true;
}
export const INITIAL_SIGNIN_ERROR: SigninError | null = parseSigninError(initialHash);
/** An invite link (#invite=<secret>) this page was opened with; the redesigned sign-in screen shows InviteView for it. */
export const INITIAL_INVITE: string | null = parseInvite(initialHash);
if (hasAuthFragment(initialHash) && typeof window !== 'undefined') {
  const url = new URL(window.location.href);
  url.hash = '';
  window.history.replaceState(null, '', url.toString());
}

interface MeStore {
  status: MeStatus;
  me: Me | null;
  providers: AuthProviders | null;
  error: UiError | null;
  /** Sign-in error from a provider callback, until dismissed. */
  signinError: SigninError | null;
  /** Invite secret from #invite=, until used or dismissed. */
  pendingInvite: string | null;
  /** Why the hub ended this browser's live stream, when that is what signed it out (SD-2). */
  endedBy: MeEndedBy | null;
  load: () => Promise<void>;
  setSignedOut: () => void;
  /** The hub ended the live stream: this browser is no longer signed in, and this is why. */
  setStreamEnded: (reason: MeEndedBy) => void;
  /** A sign-in that failed outside a provider callback (a bad owner link: 'bad-link', 'rate-limited'; `link` is the link that failed). */
  setSigninError: (e: { code: string; login: string | null; provider?: SigninError['provider']; link?: string }) => void;
  clearSigninError: () => void;
  clearPendingInvite: () => void;
}

let seq = 0;

export const useMe = create<MeStore>()((set) => ({
  status: 'loading',
  me: null,
  providers: null,
  error: null,
  signinError: INITIAL_SIGNIN_ERROR,
  pendingInvite: INITIAL_INVITE,
  endedBy: null,
  async load() {
    const req = ++seq;
    const providers = await authClient.providers().catch(() => null);
    // A hub that cannot list its methods (an older hub answers 401, or the call failed) still
    // takes the owner link, so the sign-in screen never waits on a list that is not coming.
    const fallback: AuthProviders = { github: { configured: false }, ownerLink: true };
    try {
      const me = await authClient.me();
      if (req === seq) set({ status: 'signed-in', me, providers: providers ?? fallbackProviders(me), error: null, endedBy: null });
    } catch (e) {
      if (req !== seq) return;
      const status = e instanceof ApiError ? e.status : undefined;
      if (status === 401) set({ status: 'signed-out', me: null, providers: providers ?? fallback, error: null });
      else if (status === 404) set({ status: 'unsupported', me: null, providers: providers ?? fallback, error: null });
      // Nothing answered: keep the methods the hub last reported, so the sign-in screen shows
      // them (switched off until it answers) instead of losing Sign in with GitHub.
      else set((s) => ({ status: 'error', me: null, providers: providers ?? s.providers ?? fallback, error: toUiError(e) }));
    }
  },
  setSignedOut: () => {
    seq++;
    set({ status: 'signed-out', me: null, endedBy: null });
  },
  setStreamEnded: (reason) => {
    seq++;
    set({ status: 'signed-out', me: null, endedBy: reason });
  },
  setSigninError: (e) => set({ signinError: { provider: null, ...e } }),
  clearSigninError: () => set({ signinError: null }),
  clearPendingInvite: () => set({ pendingInvite: null }),
}));

/** When /api/auth/providers failed but /api/me answered: the same picture from /api/me. */
export function fallbackProviders(me: Me): AuthProviders {
  return { github: { configured: me.providers.github }, ownerLink: true, ...(me.providers.list ? { list: me.providers.list } : {}) };
}

/** True when the signed-in role is at least `min`. Unknown (no auth module): true, the hub decides. */
export function useCan(min: Role): boolean {
  return useMe((s) => (s.status === 'unsupported' ? true : s.me ? roleAtLeast(s.me.actor.role, min) : false));
}
